import {
  ArrowLeftRight,
  ArrowRight,
  Barcode,
  Banknote,
  Bot,
  Building2,
  Clock,
  Coins,
  CreditCard,
  Crown,
  Droplets,
  FileText,
  Fuel,
  Landmark,
  MoveHorizontal,
  Nfc,
  Percent,
  Puzzle,
  ReceiptText,
  Settings,
  SlidersHorizontal,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { SectionCard } from '@/components/ui/card';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn } from '@/lib/utils';

interface Enlace {
  /** Ruta de la pantalla en EDSuite Web. */
  to: string;
  label: string;
  descripcion: string;
  icon: LucideIcon;
  /** Aviso de plan (ej. NFC es PREMIUM). */
  etiqueta?: string;
}

interface Grupo {
  titulo: string;
  icon: LucideIcon;
  enlaces: Enlace[];
}

/**
 * Enlaces del panel anterior, en el mismo orden y con las mismas descripciones
 * que `application/views/configuration/index.php`. Todas las pantallas ya viven
 * en EDSuite Web, así que el hub no tiene enlaces al panel anterior.
 */
const GRUPOS: readonly Grupo[] = [
  {
    titulo: 'Avanzado',
    icon: SlidersHorizontal,
    enlaces: [
      {
        to: '/configuracion/turnos',
        label: 'Turnos',
        descripcion: 'Gestión de turnos de trabajo del personal',
        icon: Clock,
      },
      {
        to: '/configuracion/maquinas',
        label: 'Máquina',
        descripcion: 'Configuración de surtidores y dispensadores',
        icon: Fuel,
      },
      {
        to: '/configuracion/lados',
        label: 'Lados de máquinas',
        descripcion: 'Configuración de lados y posiciones de surtidores',
        icon: MoveHorizontal,
      },
      {
        to: '/configuracion/mangueras',
        label: 'Asignar mangueras',
        descripcion: 'Configurar mangueras y productos de combustible',
        icon: Droplets,
      },
      {
        to: '/configuracion/ric-automation',
        label: 'Automatización de RIC',
        descripcion: 'Configurar la toma automática de medidas de tanques',
        icon: Bot,
      },
    ],
  },
  {
    titulo: 'General',
    icon: Settings,
    enlaces: [
      {
        to: '/configuracion/bancos',
        label: 'Listado de bancos',
        descripcion: 'Entidades bancarias disponibles',
        icon: Landmark,
      },
      {
        to: '/configuracion/cuentas-bancarias',
        label: 'Listado de cuentas bancarias',
        descripcion: 'Cuentas de la empresa para transacciones',
        icon: Building2,
      },
      {
        to: '/configuracion/tarjetas',
        label: 'Listado de tarjetas',
        descripcion: 'Tarjetas de crédito y débito aceptadas',
        icon: CreditCard,
      },
      {
        to: '/configuracion/monedas',
        label: 'Lista de monedas',
        descripcion: 'Monedas y tipos de cambio',
        icon: Coins,
      },
    ],
  },
  {
    titulo: 'Ingresos / Egresos',
    icon: ArrowLeftRight,
    enlaces: [
      {
        to: '/configuracion/pagos-cpe',
        label: 'Pagos CPE',
        descripcion: 'Métodos de pago para comprobantes electrónicos',
        icon: ReceiptText,
      },
      {
        to: '/configuracion/metodos-pago',
        label: 'Métodos de pago - Ventas',
        descripcion: 'Formas de pago disponibles en las ventas',
        icon: Wallet,
      },
      {
        to: '/configuracion/metodos-pago-detraccion',
        label: 'Métodos de pago - Detracción',
        descripcion: 'Medios de pago para operaciones con detracción',
        icon: Banknote,
      },
      {
        to: '/configuracion/tipos-detraccion',
        label: 'Tipos de Detracción',
        descripcion: 'Porcentajes y códigos de detracción',
        icon: Percent,
      },
      {
        to: '/configuracion/nfc',
        label: 'Gestión de NFC',
        descripcion: 'Dispositivos NFC para el acceso de clientes',
        icon: Nfc,
        etiqueta: 'PREMIUM',
      },
    ],
  },
  {
    titulo: 'Documentos Fiscales',
    icon: FileText,
    enlaces: [
      {
        to: '/configuracion/tipos-documento',
        label: 'Tipos de Documentos',
        descripcion: 'Facturas, boletas y demás comprobantes',
        icon: FileText,
      },
      {
        to: '/configuracion/series-documento',
        label: 'Series de Documentos',
        descripcion: 'Series y correlativos de los comprobantes',
        icon: Barcode,
      },
    ],
  },
  {
    titulo: 'Empresa',
    icon: Building2,
    enlaces: [
      {
        to: '/configuracion/empresa',
        label: 'Empresa',
        descripcion: 'Datos y configuración general de la empresa',
        icon: Building2,
      },
    ],
  },
  {
    titulo: 'Planes y Módulos',
    icon: Crown,
    enlaces: [
      {
        to: '/configuracion/planes',
        label: 'Gestión de Planes',
        descripcion: 'Planes de suscripción y sus módulos',
        icon: Crown,
      },
      {
        to: '/configuracion/modulos',
        label: 'Módulos del Sistema',
        descripcion: 'Catálogo completo de módulos disponibles',
        icon: Puzzle,
      },
    ],
  },
];

/** Tarjeta de un acceso del hub. */
function TarjetaEnlace({ enlace }: { enlace: Enlace }) {
  const contenido = (
    <>
      <span className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100"
        >
          <enlace.icon className="size-4.5" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-ink-900">{enlace.label}</span>
            {enlace.etiqueta ? <Badge variant="warning">{enlace.etiqueta}</Badge> : null}
          </span>
          <span className="mt-0.5 block text-[12px] leading-relaxed text-ink-500">
            {enlace.descripcion}
          </span>
        </span>
      </span>

      <span className="mt-3 flex items-center justify-between border-t border-ink-100 pt-2 text-xs">
        <span className="flex items-center gap-1 font-semibold text-brand-600">
          Abrir en este panel
          <ArrowRight
            aria-hidden="true"
            className="size-3.5 transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </span>
    </>
  );

  const clases = cn(
    'group flex flex-col rounded-card border border-ink-100 bg-white p-3 shadow-soft',
    'transition-shadow duration-200 hover:shadow-card',
  );

  return (
    <Link to={enlace.to} className={clases}>
      {contenido}
    </Link>
  );
}

/**
 * Configuración (`/configuracion`).
 *
 * Es el hub del panel anterior (`views/configuration/index.php`): un índice de los
 * accesos de administración, ya todos migrados a este panel.
 */
export function ConfiguracionPage() {
  useDocumentTitle('Configuración');

  const total = GRUPOS.reduce((suma, grupo) => suma + grupo.enlaces.length, 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Settings className="size-6 text-brand-500" aria-hidden="true" />
            Configuración
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Ajustes generales del sistema: catálogos, documentos, empresa, equipos y planes.
          </p>
        </div>

        <Badge variant="outline">{total} pantallas</Badge>
      </header>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {GRUPOS.map((grupo) => (
          <SectionCard
            key={grupo.titulo}
            title={grupo.titulo}
            action={<grupo.icon className="size-4 text-ink-400" aria-hidden="true" />}
          >
            <div className="space-y-3">
              {grupo.enlaces.map((enlace) => (
                <TarjetaEnlace key={enlace.label} enlace={enlace} />
              ))}
            </div>
          </SectionCard>
        ))}
      </div>
    </div>
  );
}
